import {
  migratePessoaRowToCorretor,
  pickCorretorRowFields,
} from "./compromissoFormDataNormalize";

const toSnakeCaseLower = (value) =>
  value.replace(/-/g, "_").toLowerCase();

const COMPROMISSO_PESSOA_CONJUGE_KEYS = [
  "NOME_CONJUGE",
  "CPF_CONJUGE",
  "RG_CONJUGE",
  "NACIONALIDADE_CONJUGE",
  "PROFISSAO_CONJUGE",
  "EMAIL_CONJUGE",
];

/**
 * Backend expects each vendedor/comprador with `conjuge: null` (non-casado) or
 * `conjuge: { NOME_CONJUGE, ... }` (casado), not flat cônjuge fields.
 */
function mapPessoaRowForCompromissoPayload(person) {
  const conjugeFields = {};
  COMPROMISSO_PESSOA_CONJUGE_KEYS.forEach((k) => {
    conjugeFields[k] = person[k];
  });

  const rest = { ...person };
  COMPROMISSO_PESSOA_CONJUGE_KEYS.forEach((k) => {
    delete rest[k];
  });

  if (rest.ESTADO_CIVIL_PESSOA_FISICA === "casado") {
    return {
      ...rest,
      conjuge: Object.fromEntries(
        COMPROMISSO_PESSOA_CONJUGE_KEYS.map((k) => [
          k,
          conjugeFields[k] ?? "",
        ])
      ),
    };
  }

  return {
    ...rest,
    conjuge: null,
  };
}

function transformCompromissoCompraVendaData(data) {
  const next = { ...data };
  if (Array.isArray(next.vendedores)) {
    next.vendedores = next.vendedores.map(mapPessoaRowForCompromissoPayload);
  }
  if (Array.isArray(next.compradores)) {
    next.compradores = next.compradores.map(mapPessoaRowForCompromissoPayload);
  }
  if (Array.isArray(next.captantes)) {
    next.captantes = next.captantes.map(mapPessoaRowForCompromissoPayload);
  }
  if (Array.isArray(next.intermediadoras)) {
    next.intermediadoras = next.intermediadoras.map(
      mapPessoaRowForCompromissoPayload
    );
  }
  if (next.tipo_pagamento && typeof next.tipo_pagamento === "object") {
    next.tipo_pagamento = {
      type: next.tipo_pagamento.type,
      fields: next.tipo_pagamento.fields
        ? { ...next.tipo_pagamento.fields }
        : {},
    };
  }
  return next;
}

function transformAutorizacaoLocacaoImovelData(data) {
  const next = { ...data };
  if (Array.isArray(next.proprietarios)) {
    next.proprietarios = next.proprietarios.map(mapPessoaRowForCompromissoPayload);
  }
  if (next.intermediador && typeof next.intermediador === "object") {
    next.intermediador = {
      type: next.intermediador.type,
      fields: next.intermediador.fields
        ? { ...next.intermediador.fields }
        : {},
    };
  }

  // Backend / storage may use snake_case; template placeholders use UPPER_CASE.
  const cidade =
    next.CIDADE_ASSINATURA !== undefined && next.CIDADE_ASSINATURA !== null
      ? next.CIDADE_ASSINATURA
      : next.cidade_assinatura;
  const dataAssinatura =
    next.DATA_ASSINATURA !== undefined && next.DATA_ASSINATURA !== null
      ? next.DATA_ASSINATURA
      : next.data_assinatura;

  if (cidade !== undefined && cidade !== null) {
    next.CIDADE_ASSINATURA = cidade;
    next.cidade_assinatura = cidade;
  }
  if (dataAssinatura !== undefined && dataAssinatura !== null) {
    next.DATA_ASSINATURA = dataAssinatura;
    next.data_assinatura = dataAssinatura;
  }

  return next;
}

function transformContratoLocacaoImovelData(data) {
  const next = { ...data };
  delete next.FIADOR;

  const isFiadorClause =
    String(next.clausula_garantia?.type ?? "").toLowerCase() === "fiador";

  if (Array.isArray(next.locadores)) {
    next.locadores = next.locadores.map(mapPessoaRowForCompromissoPayload);
  }
  if (Array.isArray(next.locatarios)) {
    next.locatarios = next.locatarios.map(mapPessoaRowForCompromissoPayload);
  }

  if (isFiadorClause) {
    if (Array.isArray(next.fiadores)) {
      next.fiadores = next.fiadores.map(mapPessoaRowForCompromissoPayload);
    }
  } else {
    next.fiadores = "Sem Fiadores";
  }

  if (next.clausula_garantia && typeof next.clausula_garantia === "object") {
    next.clausula_garantia = {
      type: next.clausula_garantia.type,
      fields: next.clausula_garantia.fields
        ? { ...next.clausula_garantia.fields }
        : {},
    };
  }
  return next;
}

function transformContratoParceriaVendasData(data) {
  const next = { ...data };
  if (Array.isArray(next.captantes)) {
    next.captantes = next.captantes
      .slice(0, 1)
      .map((row) => pickCorretorRowFields(migratePessoaRowToCorretor(row)));
  }
  if (Array.isArray(next.intermediadores)) {
    next.intermediadores = next.intermediadores
      .slice(0, 1)
      .map((row) => pickCorretorRowFields(migratePessoaRowToCorretor(row)));
  }
  return next;
}

export const buildContractPayload = ({
  templateKey,
  title,
  formData,
  fillingType = "standard",
  schema,
}) => {
  const dynamicFields = [];

  // Detect dynamic sections from schema
  schema.forEach((section) => {
    if (section.repeatable || section.type === "conditional") {
      dynamicFields.push(section.groupKey);
    }
  });

  const normalizedKey = toSnakeCaseLower(templateKey);
  let data = formData;
  if (
    normalizedKey === "compromisso_compra_venda" ||
    normalizedKey === "proposta_compra_imovel"
  ) {
    data = transformCompromissoCompraVendaData({ ...formData });
  } else if (normalizedKey === "contrato_locacao_imovel") {
    data = transformContratoLocacaoImovelData({ ...formData });
  } else if (normalizedKey === "autorizacao_locacao_imovel") {
    data = transformAutorizacaoLocacaoImovelData({ ...formData });
  } else if (normalizedKey === "autorizacao_gleba_loteamento") {
    data = transformAutorizacaoLocacaoImovelData({ ...formData });
  } else if (normalizedKey === "autorizacao_venda_imovel") {
    data = transformAutorizacaoLocacaoImovelData({ ...formData });
  } else if (normalizedKey === "contrato_parceria_vendas") {
    data = transformContratoParceriaVendasData({ ...formData });
  }

  const forcedDynamicFieldsByTemplate = {
    contrato_locacao_imovel: [
      "locadores",
      "locatarios",
      "fiadores",
      "testemunhas",
      "tipo_imovel",
      "clausula_garantia",
    ],
    autorizacao_locacao_imovel: [
      "proprietarios",
      "testemunhas",
      "tipo_imovel",
      "intermediador",
    ],
    proposta_compra_imovel: [
      "vendedores",
      "compradores",
      "testemunhas",
      "tipo_imovel",
    ],
    contrato_parceria_vendas: ["captantes", "intermediadores", "testemunhas"],
    autorizacao_gleba_loteamento: ["proprietarios", "intermediador"],
    autorizacao_venda_imovel: [
      "proprietarios",
      "testemunhas",
      "tipo_imovel",
      "intermediador",
    ],
  };

  let resolvedDynamicFields =
    forcedDynamicFieldsByTemplate[normalizedKey] || dynamicFields;

  if (normalizedKey === "contrato_locacao_imovel") {
    const isFiadorClause =
      String(formData?.clausula_garantia?.type ?? "").toLowerCase() ===
      "fiador";
    if (!isFiadorClause) {
      resolvedDynamicFields = resolvedDynamicFields.filter(
        (k) => k !== "fiadores"
      );
    }
  }

  return {
    templateKey: normalizedKey,
    title,
    fillingType,
    dynamic_fields: resolvedDynamicFields,
    data,
  };
};